import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { useSnackbar } from "notistack";
import { Button } from "@accredo/fe-shared/theme/components";
import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { SEARCH_WORD_KEY } from "../tables/utils/tableUtils.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import { usePaymentImportActions } from "hooks/usePaymentImportActions.jsx";

const MATCH_TYPES = [
  {
    value: "exact",
    label: "Exakt",
    tooltip: "Schadennummer im Verwendungszweck, Betrag passt",
  },
  {
    value: "possible",
    label: "Möglich",
    tooltip: "Betrag passt, keine Schadennummer",
  },
  {
    value: "partial",
    label: "Teilzahlung",
    tooltip: "Schadennummer im Verwendungszweck, Betrag passt nicht (50 %)",
  },
];

const MAX_UNMATCHED = 50;
const DEFAULT_UNMATCHED = 2;

const formatAmount = (value) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    Number(value) || 0
  );

const countOpenOutBookings = (schaden) =>
  (schaden.bookings || []).filter(
    (booking) => booking.type === "OUT" && booking.status === "pending"
  ).length;

const getExportClaimNumbers = (exportItem) =>
  [
    ...new Set(
      (exportItem.bookings || [])
        .map((booking) => booking.schaden?.schaden_nummer_accredo)
        .filter(Boolean)
    ),
  ].join(", ") || "Ohne Schaden";

const clampCount = (value) =>
  Math.min(MAX_UNMATCHED, Math.max(1, Number.parseInt(value, 10) || 1));

/**
 * Test-only generator for bank statement files (see
 * docs/plan/acc-222-import-simulation.md). Builds the request for
 * `data/payment-imports/simulate` and downloads the returned file; the file is
 * not imported automatically.
 */
export default function PaymentImportSimulationDialog({ open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const { simulatePaymentImport } = usePaymentImportActions();

  const [format, setFormat] = useState("xml");
  const [claims, setClaims] = useState([]);
  const [exports, setExports] = useState([]);
  const [creditUnmatchedEnabled, setCreditUnmatchedEnabled] = useState(false);
  const [creditUnmatchedCount, setCreditUnmatchedCount] =
    useState(DEFAULT_UNMATCHED);
  const [debitUnmatchedEnabled, setDebitUnmatchedEnabled] = useState(false);
  const [debitUnmatchedCount, setDebitUnmatchedCount] =
    useState(DEFAULT_UNMATCHED);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Claim search
  const [claimQuery, setClaimQuery] = useState("");
  const [claimOptions, setClaimOptions] = useState([]);
  const [isClaimSearchLoading, setIsClaimSearchLoading] = useState(false);

  // Open booking exports
  const [exportOptions, setExportOptions] = useState([]);
  const [isExportsLoading, setIsExportsLoading] = useState(false);

  const reset = () => {
    setFormat("xml");
    setClaims([]);
    setExports([]);
    setCreditUnmatchedEnabled(false);
    setCreditUnmatchedCount(DEFAULT_UNMATCHED);
    setDebitUnmatchedEnabled(false);
    setDebitUnmatchedCount(DEFAULT_UNMATCHED);
    setClaimQuery("");
    setClaimOptions([]);
  };

  useEffect(() => {
    if (!open) return;

    backendGet("data/bookingexports", {
      label: "Offene Export-Pakete",
      setIsLoading: setIsExportsLoading,
      enqueueSnackbar,
      options: {
        params: {
          api_version: "v2",
          filter: { status: "created" },
          include: ["bookings", "schaden"],
          page: 1,
          limit: 100,
        },
      },
      setResult: (res) => setExportOptions(res?.data || []),
    });
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (claimQuery.length < 2) return;

    const timeout_id = setTimeout(() => {
      backendGet("data/schadens", {
        label: "Schadensuche",
        setIsLoading: setIsClaimSearchLoading,
        enqueueSnackbar,
        options: {
          params: {
            filter: { [SEARCH_WORD_KEY]: claimQuery },
            include: ["gesellschaft", "bookings"],
            page: 1,
            limit: 20,
          },
        },
        setResult: (res) => setClaimOptions(res?.data || []),
      });
    }, 400);

    return () => clearTimeout(timeout_id);
  }, [claimQuery, enqueueSnackbar]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleAddClaim = (_event, schaden) => {
    if (!schaden) return;
    if (claims.some((claim) => claim.schaden.id === schaden.id)) return;

    setClaims((current) => [...current, { schaden, match_type: "exact" }]);
    setClaimQuery("");
    setClaimOptions([]);
  };

  const handleChangeMatchType = (schaden_id, match_type) => {
    if (!match_type) return;

    setClaims((current) =>
      current.map((claim) =>
        claim.schaden.id === schaden_id ? { ...claim, match_type } : claim
      )
    );
  };

  const handleRemoveClaim = (schaden_id) => {
    setClaims((current) =>
      current.filter((claim) => claim.schaden.id !== schaden_id)
    );
  };

  const hasDuplicateExportSums = useMemo(() => {
    const sums = exports.map((exportItem) => Number(exportItem.summe));
    return new Set(sums).size !== sums.length;
  }, [exports]);

  const canSubmit =
    claims.length > 0 ||
    exports.length > 0 ||
    creditUnmatchedEnabled ||
    debitUnmatchedEnabled;

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const result = await simulatePaymentImport({
      format,
      claims: claims.map((claim) => ({
        schaden_id: claim.schaden.id,
        match_type: claim.match_type,
      })),
      booking_export_ids: exports.map((exportItem) => exportItem.id),
      unmatched_credit_count: creditUnmatchedEnabled ? creditUnmatchedCount : 0,
      unmatched_debit_count: debitUnmatchedEnabled ? debitUnmatchedCount : 0,
    });

    setIsSubmitting(false);

    if (result) {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        Import simulieren
        <Chip label="Nur Testfunktion" color="warning" size="small" />
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Alert severity="warning">
            Diese Funktion erzeugt eine künstliche Kontoauszugsdatei zum Testen
            des Zahlungsimports. Die Datei wird nur heruntergeladen und nicht
            importiert.
          </Alert>

          {/* Format */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Dateiformat
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={format}
              onChange={(_event, value) => value && setFormat(value)}
            >
              <ToggleButton value="xml">camt (.xml)</ToggleButton>
              <ToggleButton value="sta">MT940 (.sta)</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          {/* Einzahlungen */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Einzahlungen</Typography>

            <Autocomplete
              options={claimOptions}
              filterOptions={(options) =>
                options.filter(
                  (option) =>
                    !claims.some((claim) => claim.schaden.id === option.id)
                )
              }
              loading={isClaimSearchLoading}
              value={null}
              inputValue={claimQuery}
              onInputChange={(_event, value, reason) => {
                if (reason === "reset") return;
                setClaimQuery(value);
                if (value.length < 2) setClaimOptions([]);
              }}
              onChange={handleAddClaim}
              noOptionsText={
                claimQuery.length < 2
                  ? "Mindestens 2 Zeichen eingeben..."
                  : "Keine Schäden gefunden"
              }
              getOptionLabel={(schaden) => schaden.schaden_nummer_accredo || ""}
              renderOption={(option_props, schaden) => {
                const { key, ...rest_props } = option_props;
                return (
                  <Box component="li" key={key} {...rest_props}>
                    <Box>
                      <Typography variant="body2">
                        {schaden.schaden_nummer_accredo}
                        {schaden.schaden_nummer_vu &&
                          ` | ${schaden.schaden_nummer_vu}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {schaden.gesellschaft?.name || "Kein Versicherer"}
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Schaden hinzufügen"
                  placeholder="Schadennummer suchen..."
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isClaimSearchLoading && (
                            <CircularProgress color="inherit" size={20} />
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />

            {claims.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {claims.map(({ schaden, match_type }) => {
                  const open_bookings = countOpenOutBookings(schaden);
                  return (
                    <Box
                      key={schaden.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 1.5,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2">
                          {schaden.schaden_nummer_accredo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {schaden.gesellschaft?.name || "Kein Versicherer"} ·{" "}
                          {open_bookings > 0
                            ? `${open_bookings} Einzahlung${open_bookings === 1 ? "" : "en"} (je offene Auszahlungsbuchung)`
                            : "1 Einzahlung (Schadenhöhe)"}
                        </Typography>
                      </Box>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={match_type}
                        onChange={(_event, value) =>
                          handleChangeMatchType(schaden.id, value)
                        }
                      >
                        {MATCH_TYPES.map((type) => (
                          <Tooltip key={type.value} title={type.tooltip}>
                            <ToggleButton value={type.value}>
                              {type.label}
                            </ToggleButton>
                          </Tooltip>
                        ))}
                      </ToggleButtonGroup>
                      <Tooltip title="Entfernen">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveClaim(schaden.id)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  );
                })}
                {claims.some((claim) => claim.match_type === "possible") && (
                  <Typography variant="caption" color="text.secondary">
                    Hinweis: Eine mögliche Einzahlung kann auch bei anderen
                    Schäden mit demselben Betrag als „möglich“ auftauchen.
                  </Typography>
                )}
              </Box>
            )}

            <UnmatchedCountField
              label="Zusätzliche Einzahlungen ohne Zuordnung"
              enabled={creditUnmatchedEnabled}
              onChangeEnabled={setCreditUnmatchedEnabled}
              count={creditUnmatchedCount}
              onChangeCount={setCreditUnmatchedCount}
            />
          </Box>

          <Divider />

          {/* Auszahlungen */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Auszahlungen</Typography>

            <Autocomplete
              multiple
              options={exportOptions}
              loading={isExportsLoading}
              value={exports}
              onChange={(_event, value) => setExports(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={getExportClaimNumbers}
              noOptionsText="Keine offenen Export-Pakete"
              renderOption={(option_props, exportItem) => {
                const { key, ...rest_props } = option_props;
                return (
                  <Box component="li" key={key} {...rest_props}>
                    <Box>
                      <Typography variant="body2">
                        {getExportClaimNumbers(exportItem)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getDatum(exportItem.created_at)} ·{" "}
                        {formatAmount(exportItem.summe)} ·{" "}
                        {(exportItem.bookings || []).length} Buchung(en)
                      </Typography>
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Offene Export-Pakete"
                  helperText="Pro Export-Paket wird eine Sammelbuchung über die Paketsumme erzeugt. Sie muss nach dem Import in der Zahlungszuordnung manuell dem Paket zugeordnet werden."
                />
              )}
            />

            {hasDuplicateExportSums && (
              <Alert severity="info">
                Mehrere gewählte Export-Pakete haben dieselbe Summe. Die
                Vorauswahl im Zuordnungsdialog ist dann nicht eindeutig.
              </Alert>
            )}

            <UnmatchedCountField
              label="Zusätzliche Auszahlungen ohne Zuordnung"
              enabled={debitUnmatchedEnabled}
              onChangeEnabled={setDebitUnmatchedEnabled}
              count={debitUnmatchedCount}
              onChangeCount={setDebitUnmatchedCount}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="tertiary" onClick={handleClose}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
        >
          Datei erzeugen & herunterladen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

PaymentImportSimulationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

function UnmatchedCountField({
  label,
  enabled,
  onChangeEnabled,
  count,
  onChangeCount,
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(event) => onChangeEnabled(event.target.checked)}
          />
        }
        label={label}
        sx={{ flex: 1 }}
      />
      <TextField
        type="number"
        size="small"
        label="Anzahl"
        value={count}
        disabled={!enabled}
        onChange={(event) => onChangeCount(clampCount(event.target.value))}
        slotProps={{ htmlInput: { min: 1, max: MAX_UNMATCHED } }}
        sx={{ width: 100 }}
      />
    </Box>
  );
}

UnmatchedCountField.propTypes = {
  label: PropTypes.string.isRequired,
  enabled: PropTypes.bool.isRequired,
  onChangeEnabled: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  onChangeCount: PropTypes.func.isRequired,
};
